import React from 'react';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { getUpdatedJobTime } from './CrewTimesheet';
import { fromUtcToLocalMoment } from '../utilities/DateHelpers';

export default class TimesheetDateTimeButton extends React.Component {
    constructor(props) {
        super(props);

        this.state = { showModal: false };

        this.toggleModal = this.toggleModal.bind(this);
    }

    toggleModal() {
        const { showModal } = this.state;

        this.setState({ showModal: !showModal });
    }

    toHourDisplay(hourValue) {
        return fromUtcToLocalMoment(hourValue).format('hh:mm A');
    }

    toHourEdit(hourValue) {
        return fromUtcToLocalMoment(hourValue).format('HH:mm');
    }

    onChangeStartTime = (e) => {
        if (!e || !e.target) {
            return;
        }

        const startTimeValue = e.target.value;

        const { timesheet, isAdmin, onTimesheetMutated } = this.props;

        const startTime = getUpdatedJobTime(timesheet, startTimeValue);

        const startTimeBefore = timesheet.startTime;

        if (isAdmin) {
            timesheet.adjustedStartTime = startTime;

            if (!timesheet.adjustedEndTime) {
                timesheet.adjustedEndTime = timesheet.endTime;
            }
        } else {
            timesheet.startTime = startTime;
        }

        timesheet.timesheetDetails.forEach((td) => {
            if (
                !td.start ||
                moment(td.adjustedStart || td.start).isSame(
                    startTimeBefore,
                    'minute'
                )
            ) {
                if (isAdmin) {
                    td.adjustedStart = startTime;
                } else {
                    td.start = startTime;
                }
            }
        });

        onTimesheetMutated(timesheet);
    };

    onChangeEndTime = (e) => {
        if (!e || !e.target) {
            return;
        }

        const endTimeValue = e.target.value;

        const { timesheet, isAdmin, onTimesheetMutated } = this.props;

        const endTime = getUpdatedJobTime(timesheet, endTimeValue);

        const endTimeBefore = timesheet.endTime;

        if (isAdmin) {
            timesheet.adjustedEndTime = endTime;

            if (!timesheet.adjustedStartTime) {
                timesheet.adjustedStartTime = timesheet.startTime;
            }
        } else {
            timesheet.endTime = endTime;
        }

        timesheet.timesheetDetails.forEach((td) => {
            if (isAdmin) {
                if (
                    !td.end ||
                    moment(td.adjustedEnd || td.end).isSame(
                        endTimeBefore,
                        'minute'
                    )
                ) {
                    td.adjustedEnd = endTime;

                    if (!td.adjustedStart) {
                        td.adjustedStart = td.start;
                    }
                }
            } else {
                if (!td.end || moment(td.end).isSame(endTimeBefore, 'minute')) {
                    td.end = endTime;
                }
            }
        });

        onTimesheetMutated(timesheet);
    };

    render() {
        const { loading, timesheet, isReadOnly } = this.props;

        const { showModal } = this.state;

        if (loading || !timesheet) {
            return null;
        }

        const displayStartTime = this.toHourDisplay(
            timesheet.adjustedStartTime ?? timesheet.startTime
        );

        const timesheetEndTime = timesheet.adjustedEndTime ?? timesheet.endTime;

        const displayEndTime = timesheetEndTime
            ? this.toHourDisplay(timesheetEndTime)
            : null;

        const incomplete = !displayEndTime;

        const startTime = this.toHourEdit(
            timesheet.adjustedStartTime ?? timesheet.startTime
        );

        const originalStartTime = this.toHourEdit(timesheet.startTime);

        const endTime =
            timesheet.adjustedEndTime || timesheet.endTime
                ? this.toHourEdit(
                      timesheet.adjustedEndTime ?? timesheet.endTime
                  )
                : null;

        const originalEndTime = timesheet.endTime
            ? this.toHourEdit(timesheet.endTime)
            : '';

        const hasBeenAdjusted = Boolean(
            timesheet.adjustedStartTime || timesheet.adjustedEndTime
        );

        const timeValuesSet = Boolean(
            (timesheet.startTime && timesheet.endTime) ||
            (timesheet.adjustedStartTime && timesheet.adjustedEndTime)
        );

        const complete = timeValuesSet && endTime > startTime;

        return (
            <div className="mb-2 col-2">
                <Card
                    onClick={this.toggleModal}
                    className={cls('date-time-card', {
                        'lifted-effect': incomplete,
                    })}
                >
                    <CardHeader className={cls({ complete })}>
                        {moment(timesheet.jobDate).format(
                            MomentDateFormat.WeekDayNameAndDate
                        )}

                        {hasBeenAdjusted && (
                            <FontAwesomeIcon icon={faEdit} className="ml-2" />
                        )}
                    </CardHeader>
                    <CardBody
                        className={cls({
                            'text-danger': !timeValuesSet,
                        })}
                    >
                        {displayStartTime} -{' '}
                        {displayEndTime ? displayEndTime : 'Not Set'}
                    </CardBody>
                </Card>

                <Modal
                    isOpen={showModal}
                    fade={false}
                    autoFocus={false}
                    size="xl"
                >
                    <ModalHeader toggle={this.toggleModal}>
                        Shared Start/End Time
                    </ModalHeader>
                    <ModalBody>
                        <div className="d-flex">
                            <div className="col-6">
                                <Label for="start-time">Start Time</Label>
                                <Input
                                    id="start-time"
                                    type="time"
                                    step={900}
                                    value={startTime}
                                    onChange={this.onChangeStartTime}
                                    disabled={isReadOnly}
                                />
                            </div>
                            <div className="col-6">
                                <Label for="end-time">End Time</Label>
                                <Input
                                    id="end-time"
                                    type="time"
                                    step={900}
                                    value={endTime ?? ''}
                                    onChange={this.onChangeEndTime}
                                    autoFocus={true}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                    </ModalBody>
                    {Boolean(
                        timesheet.adjustedStartTime || timesheet.adjustedEndTime
                    ) && (
                        <ModalBody>
                            <div className="d-flex">
                                <div className="col-6">
                                    <Label>Original Start Time</Label>
                                    <Input
                                        type="time"
                                        value={originalStartTime}
                                        disabled={true}
                                    />
                                </div>
                                <div className="col-6">
                                    <Label for="end-time">
                                        Original End Time
                                    </Label>
                                    <Input
                                        type="time"
                                        value={originalEndTime}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </ModalBody>
                    )}
                    <ModalFooter>
                        <Button
                            type="button"
                            color="success"
                            onClick={this.toggleModal}
                            disabled={!startTime || !endTime}
                            hidden={isReadOnly}
                        >
                            Update
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}
